import type { CompanyInfo, MaterialItem, MaterialSelection } from '@generated/types';

export interface ValidationResult {
  companyInvalid: boolean;
  contactNameInvalid: boolean;
  emailInvalid: boolean;
  shippingOriginInvalid: boolean;
  desiredShipmentDateInvalid: boolean;
  signatoryNameInvalid: boolean;
  signatoryTitleInvalid: boolean;
  signatureDateInvalid: boolean;
  authorizationInvalid: boolean;
  noMaterialTicked: boolean;
  materialWeightErrorIds: Set<string>;
  materialUploadErrorIds: Set<string>;
  isValid: boolean;
  summaryMessage: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function computeValidation(params: {
  company: CompanyInfo;
  desiredShipmentDate: Date | undefined;
  signatoryName: string;
  signatoryTitle: string;
  signatureDate: Date | null;
  authorizationConfirmed: boolean;
  materials: MaterialItem[];
  selections: Record<string, MaterialSelection>;
  filesByMaterial: Record<string, File[]>;
}): ValidationResult {
  const {
    company,
    desiredShipmentDate,
    signatoryName,
    signatoryTitle,
    signatureDate,
    authorizationConfirmed,
    materials,
    selections,
    filesByMaterial,
  } = params;

  const companyInvalid = company.company.trim() === '';
  const contactNameInvalid = company.contactName.trim() === '';
  const emailInvalid = !EMAIL_RE.test(company.email.trim());
  const shippingOriginInvalid = company.shippingOrigin.trim() === '';
  const desiredShipmentDateInvalid = desiredShipmentDate == null;
  const signatoryNameInvalid = signatoryName.trim() === '';
  const signatoryTitleInvalid = signatoryTitle.trim() === '';
  const signatureDateInvalid = signatureDate == null;
  const authorizationInvalid = !authorizationConfirmed;

  const tickedMaterials = materials.filter((m) => selections[m.id]?.checked);
  const noMaterialTicked = tickedMaterials.length === 0;

  const materialWeightErrorIds = new Set<string>();
  for (const m of tickedMaterials) {
    const weight = selections[m.id]?.weightLbs;
    if (!weight || weight <= 0) materialWeightErrorIds.add(m.id);
  }

  const materialUploadErrorIds = new Set<string>();
  for (const m of tickedMaterials) {
    if (m.requiresUpload && (filesByMaterial[m.id]?.length ?? 0) === 0) {
      materialUploadErrorIds.add(m.id);
    }
  }

  const hasBasicFieldErrors =
    companyInvalid ||
    contactNameInvalid ||
    emailInvalid ||
    shippingOriginInvalid ||
    desiredShipmentDateInvalid ||
    signatoryNameInvalid ||
    signatoryTitleInvalid ||
    signatureDateInvalid;

  const isValid =
    !hasBasicFieldErrors &&
    !authorizationInvalid &&
    !noMaterialTicked &&
    materialWeightErrorIds.size === 0 &&
    materialUploadErrorIds.size === 0;

  let summaryMessage: string | null = null;
  if (!isValid) {
    if (
      authorizationInvalid &&
      !hasBasicFieldErrors &&
      !noMaterialTicked &&
      materialWeightErrorIds.size === 0 &&
      materialUploadErrorIds.size === 0
    ) {
      summaryMessage = 'Check the authorization box to submit.';
    } else if (noMaterialTicked) {
      summaryMessage = 'Pick at least one material category, and fill in anything highlighted above.';
    } else if (materialWeightErrorIds.size > 0) {
      summaryMessage = 'Add a monthly weight for every material you checked.';
    } else if (materialUploadErrorIds.size > 0) {
      summaryMessage = 'Upload at least one photo or spec sheet for each material that needs review.';
    } else {
      summaryMessage = 'A few things are still needed — the highlighted fields, plus a signature and date.';
    }
  }

  return {
    companyInvalid,
    contactNameInvalid,
    emailInvalid,
    shippingOriginInvalid,
    desiredShipmentDateInvalid,
    signatoryNameInvalid,
    signatoryTitleInvalid,
    signatureDateInvalid,
    authorizationInvalid,
    noMaterialTicked,
    materialWeightErrorIds,
    materialUploadErrorIds,
    isValid,
    summaryMessage,
  };
}
